import { notifyError } from '@/core/helpers/notification.helper';
import { client } from '@/server';
import { PartnerCinema } from './partner-cinema.interface';
import { baseUserCinemaQuery } from '../user-cinema/user-cinema.service';
import { useQueryBuilder } from '@/core/composables/useQueryBuilder';
import { Filters } from '@/core/store/filter.interface';

function userCinemaMapper(row: Record<string, any>) {
   return {
      ...row,
      cinema: JSON.parse(row.cinema as string),
      watch_status: row.watch_status ? JSON.parse(row.watch_status as string) : null,
   } as PartnerCinema;
}
export class PartnerCinemaService {
   async getAll(userId: number, partnerId: number, filters: Filters) {
      const query = `
         ${baseUserCinemaQuery}
      `;
      const { buildQuery } = useQueryBuilder(filters, query);
      const filterQuery = buildQuery();
      try {
         const response = await client.execute(filterQuery);
         return response.rows.map(userCinemaMapper) as Array<PartnerCinema>;
      } catch (error) {
         console.error('Error getting partner cinemas:', error, filterQuery);
         notifyError({ title: 'Something went wrong while fetching the partner cinemas' });
      }
   }

   async get(partnerCinemaId: number) {
      const query = `
         ${baseUserCinemaQuery}
         WHERE uc.id = ?
      `;
      try {
         const response = await client.execute(query, [partnerCinemaId]);
         if (response.rows.length === 0) return null;

         return userCinemaMapper(response.rows[0] as Record<string, any>);
      } catch (error) {
         console.error('Error getting partner cinema:', error);
         notifyError({ title: 'Something went wrong while fetching the partner cinema' });
      }
   }

   async post(partnerCinema: Partial<PartnerCinema>) {
      const query = `
         INSERT INTO user_cinema (
            user_id,
            partner_id,
            cinema_id,
            cinema_type,
            watch_status_id,
            watched_season_episodes
         ) VALUES (?, ?, ?, ?, ?, ?)
         RETURNING id
      `;

      try {
         if (
            partnerCinema.user_id &&
            partnerCinema.partner_id &&
            partnerCinema.cinema_id &&
            partnerCinema.cinema_type &&
            partnerCinema.watch_status_id
         ) {
            const insertedResponse = await client.execute(query, [
               partnerCinema.user_id,
               partnerCinema.partner_id,
               partnerCinema.cinema_id,
               partnerCinema.cinema_type,
               partnerCinema.watch_status_id,
               partnerCinema.watched_season_episodes ? JSON.stringify(partnerCinema.watched_season_episodes) : null,
            ]);

            if (insertedResponse.rows.length === 0 || !insertedResponse.rows[0].id) return null;

            const response = await this.get(insertedResponse.rows[0].id as number);

            return response;
         } else {
            console.error('Error: Missing required fields for partner cinema');
            notifyError({ title: 'Missing required fields for partner cinema' });
            return null;
         }
      } catch (error) {
         console.error('Error posting partner cinema:', error);
         notifyError({ title: 'Something went wrong while posting the partner cinema' });
      }
   }

   async put(id: number, watchStatusId: number, watchedSeasonEpisodes: string) {
      const query = `
         UPDATE user_cinema
         SET
            watch_status_id = ?,
            watched_season_episodes = ?
         WHERE id = ?
         RETURNING id
      `;

      try {
         const insertedResponse = await client.execute(query, [watchStatusId, watchedSeasonEpisodes, id]);

         if (insertedResponse.rows.length === 0 || !insertedResponse.rows[0].id) return null;

         const response = await this.get(insertedResponse.rows[0].id as number);
         return response;
      } catch (error) {
         console.error('Error updating partner cinema:', error);
         notifyError({ title: 'Something went wrong while updating the partner cinema' });
      }
   }

   async delete(id: number) {
      const query = `
         DELETE FROM user_cinema
         WHERE id = ?
      `;

      try {
         await client.execute(query, [id]);
      } catch (error) {
         console.error('Error deleting partner cinema:', error);
         notifyError({ title: 'Something went wrong while deleting the partner cinema' });
      }
   }

   async getByIds(userId: number, partnerId: number, cinemaId: number, type: 'movie' | 'serie') {
      const query = `
         ${baseUserCinemaQuery}
         WHERE 
            uc.cinema_type = ?
         AND 
            uc.cinema_id = ?
         AND
            (
               (uc.user_id = ${userId} AND uc.partner_id = ${partnerId}) 
               OR 
               (uc.user_id = ${partnerId} AND uc.partner_id = ${userId})
            )
         
      `;
      try {
         const response = await client.execute(query, [type, cinemaId]);
         if (response.rows.length === 0) return null;
         return userCinemaMapper(response.rows[0] as Record<string, any>);
      } catch (error) {
         console.error('Error getting partner cinema by ids:', error);
         notifyError({ title: 'Something went wrong while fetching the partner cinema by ids' });
      }
   }
}
